import { Icon } from "@iconify/react";
import { lazy, Suspense, useRef } from "react";
import { preload } from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "motion/react";
import Button from "../ui/button";
import RevealText, { cinematicEase } from "../ui/reveal-text";
import ChatPhone from "../chat/chat-phone";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useHardwareAcceleration } from "@/hooks/use-hardware-acceleration";
import iphoneModel from "@/assets/models/iphone-model.glb";

const PhoneModel = lazy(() => import("../3d/PhoneModel"));

export default function Home() {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const hasHardwareAcceleration = useHardwareAcceleration();
  const use3dPhone = isDesktop && hasHardwareAcceleration && !reduceMotion;
  // Fetch the model alongside the 3D chunk rather than after it loads. The
  // options match the request three.js makes, so it reuses this one.
  if (use3dPhone)
    preload(iphoneModel, { as: "fetch", crossOrigin: "anonymous" });
  const animate = !reduceMotion;
  const heroRef = useRef<HTMLDivElement>(null);

  // As the hero scrolls away the copy drifts up and dissolves, like the
  // camera pulling past it.
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const copyY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const copyOpacity = useTransform(scrollYProgress, [0, 0.55], [1, 0]);
  const phoneY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  const fadeUp = (delay: number) =>
    animate
      ? {
          initial: { opacity: 0, y: 18 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.9, delay, ease: cinematicEase },
        }
      : {};

  return (
    <div
      ref={heroRef}
      className="relative flex h-full w-full max-w-6xl flex-col items-center justify-start gap-6 pb-16 md:items-start md:justify-center md:gap-8"
    >
      <motion.div
        style={animate ? { y: copyY, opacity: copyOpacity } : undefined}
        className="relative z-10 mt-12 flex flex-col items-center gap-5 md:mt-0 md:items-start md:gap-7"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -inset-x-16 top-4 -z-10 h-64 rounded-full bg-[radial-gradient(closest-side,rgba(120,190,255,0.12),transparent)] blur-2xl"
        />
        <RevealText
          as="h1"
          text="Elevating what connects us"
          delay={0.3}
          className="w-[19rem] text-center text-[2.6rem] font-normal leading-[1.04] tracking-[-0.02em] md:w-[34rem] md:text-start md:text-[clamp(3.2rem,5.6vw,4.75rem)]"
        />

        <motion.p
          {...fadeUp(0.95)}
          className="hidden max-w-[29rem] text-[15px] font-light leading-relaxed text-white/75 md:block md:text-base"
        >
          Building integrated digital communities since 2017. We blend strategy,
          design, and technology to create tailored ecosystems for modern
          brands.
        </motion.p>

        <motion.div
          {...fadeUp(1.15)}
          className="flex flex-col items-center gap-3 md:flex-row md:gap-6"
        >
          <Button
            isActive={true}
            radius="20px"
            onClick={() => navigate("/contact")}
            className="group relative flex items-center gap-2 bg-[rgba(0,0,0,0.45)] md:text-lg"
          >
            <span className="pointer-events-none absolute inset-0 -translate-x-full bg-linear-to-r from-transparent via-white/15 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full" />
            Start Ascending
            <Icon
              icon="material-symbols:arrow-outward"
              className="h-5 w-5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
            />
          </Button>
          <button
            type="button"
            onClick={() => navigate("/services")}
            className="group hidden items-center gap-2 rounded-full px-2 py-2 text-sm text-white/70 transition-colors hover:text-white focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white/70 md:flex"
          >
            See what we do
            <Icon
              icon="material-symbols:arrow-downward-rounded"
              className="text-lg transition-transform duration-300 group-hover:translate-y-0.5"
            />
          </button>
        </motion.div>
      </motion.div>

      {use3dPhone ? (
        <Suspense fallback={null}>
          <PhoneModel />
        </Suspense>
      ) : (
        <motion.div
          style={animate ? { y: phoneY } : undefined}
          className={
            isDesktop
              ? "pointer-events-none absolute bottom-1/2 left-1/2 z-0 flex h-[min(70dvw,640px)] w-1/2 translate-y-1/2 items-center justify-center"
              : "pointer-events-none absolute bottom-6 left-1/2 z-0 h-[56svh] max-h-[480px] -translate-x-1/2"
          }
        >
          <motion.div
            initial={animate ? { opacity: 0, y: 70, scale: 0.94 } : false}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.5, delay: 0.45, ease: cinematicEase }}
            className="h-full"
          >
            <ChatPhone animate={animate} className="h-full" />
          </motion.div>
        </motion.div>
      )}

      {isDesktop && (
        <motion.div
          style={animate ? { opacity: copyOpacity } : undefined}
          className="absolute bottom-8 left-0"
          aria-hidden="true"
        >
          <motion.div
            {...fadeUp(1.6)}
            className="flex items-center gap-4 text-[11px] uppercase tracking-[0.28em] text-white/45"
          >
            <span className="relative block h-10 w-px overflow-hidden bg-white/15">
              <span className="absolute inset-x-0 top-0 h-1/2 animate-[scroll-cue_2.2s_cubic-bezier(0.65,0,0.35,1)_infinite] bg-linear-to-b from-transparent via-[#8FD0FF] to-transparent" />
            </span>
            Scroll to explore
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}
